import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Button, Table, Modal, ModalHeader,
  ModalBody, ModalFooter, Input, Label, Nav, NavItem, NavLink, Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { useAppDispatch } from "src/store";
import { MKTV2, Mktv2TypeKey } from "src/store/marketing-v2/saga";
import {
  LANGS, LANG_LABEL, Lang, deinterleave, buildDataJson, localizableFields, LocField,
} from "./lang";
import HeroSlidesPanel from "./HeroSlides";

/**
 * Pazarlama Sitesi V2 — içerik yönetimi. Çoklu-dil içerik artık RAW JSON yerine
 * DİL SEKMELİ editörle (her dil ayrı) düzenlenir; kayıtta backend Translates'i senkronlar.
 * Düz kolonlar (Seo/Icon/...) normal input'larla. Boş dil kayıtta İngilizce ile dolar.
 */

type Item = any;

const TYPES: { key: Mktv2TypeKey; label: string; cols: string[]; titlePath?: string }[] = [
  // Hero slaytları kendi yapısına özgü formla yönetilir (ham JSON değil) →
  // aşağıda generic tablo yerine HeroSlidesPanel render edilir.
  { key: "heroslides", label: "Hero Slaytları", cols: ["MockTemplate", "BrowserUrl"], titlePath: "titleAccent.tr" },
  { key: "modules", label: "Modüller", cols: ["Seo", "Icon", "Color"], titlePath: "name.tr" },
  { key: "solutions", label: "Çözümler", cols: ["Seo", "Icon"], titlePath: "name.tr" },
  { key: "stats", label: "İstatistikler", cols: ["Value"], titlePath: "label.tr" },
  { key: "steps", label: "Adımlar (Nasıl çalışır)", cols: ["Icon"], titlePath: "title.tr" },
  { key: "testimonials", label: "Yorumlar", cols: ["Name", "Avatar"], titlePath: "role.tr" },
  { key: "blog", label: "Blog", cols: ["Slug", "Category", "ReadMinutes", "Author", "PublishDate", "Image"], titlePath: "title.tr" },
  { key: "helpcategories", label: "Yardım Kategorileri", cols: ["Seo", "Icon"], titlePath: "name.tr" },
  { key: "helparticles", label: "Yardım Makaleleri", cols: ["Slug", "CategorySeo"], titlePath: "title.tr" },
  { key: "faqs", label: "SSS", cols: [], titlePath: "q.tr" },
];

const dig = (obj: any, path?: string) => {
  if (!obj || !path) return "";
  try { return path.split(".").reduce((a, k) => (a == null ? a : a[k]), obj) ?? ""; }
  catch { return ""; }
};

const MarketingV2Page = () => {
  const dispatch = useAppDispatch();
  const [activeKey, setActiveKey] = useState<Mktv2TypeKey>("modules");
  const [rows, setRows] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [saving, setSaving] = useState(false);

  // Dil-bazlı düzenleme durumu (edit modu)
  const [skeleton, setSkeleton] = useState<any | null>(null);
  const [perLang, setPerLang] = useState<Record<Lang, any> | null>(null);
  const [activeLang, setActiveLang] = useState<Lang>("en");
  const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>({}); // "lang:key" → ham JSON
  const [rawJson, setRawJson] = useState<string>("{}"); // yeni kayıt fallback

  const cfg = useMemo(() => TYPES.find((t) => t.key === activeKey)!, [activeKey]);
  const fields: LocField[] = useMemo(() => (skeleton ? localizableFields(skeleton) : []), [skeleton]);

  const load = useCallback(() => {
    // Hero slaytları kendi panelinde yükleniyor; burada ikinci istek atma.
    if (activeKey === "heroslides") { setRows([]); return; }
    setLoading(true);
    dispatch(
      MKTV2[activeKey].list({
        payload: {
          onSuccess: (_m: string, p: Item[]) => { setRows(Array.isArray(p) ? p : []); setLoading(false); },
          onError: () => { setRows([]); setLoading(false); },
        },
      })
    );
  }, [dispatch, activeKey]);

  useEffect(() => { load(); }, [load]);

  const parseData = (row: Item) => {
    try { return JSON.parse(row?.DataJson || "{}"); } catch { return {}; }
  };

  const openCreate = () => {
    const blank: Item = { Order: rows.length, IsActive: true };
    cfg.cols.forEach((c) => (blank[c] = ""));
    setEditing(blank);
    setSkeleton(null); setPerLang(null); setJsonDrafts({});
    setRawJson("{}");
    setActiveLang("en");
    setOpen(true);
  };

  const openEdit = (row: Item) => {
    setEditing({ ...row });
    const skel = parseData(row);
    setSkeleton(skel);
    const pl = Object.fromEntries(LANGS.map((l) => [l, deinterleave(skel, l)])) as Record<Lang, any>;
    setPerLang(pl);
    setJsonDrafts({});
    setActiveLang("en");
    setOpen(true);
  };

  const setLocText = (key: string, val: string) =>
    setPerLang((pl) => (pl ? { ...pl, [activeLang]: { ...pl[activeLang], [key]: val } } : pl));

  const setJsonDraft = (key: string, val: string) =>
    setJsonDrafts((d) => ({ ...d, [`${activeLang}:${key}`]: val }));

  const draftFor = (key: string): string => {
    const k = `${activeLang}:${key}`;
    if (k in jsonDrafts) return jsonDrafts[k];
    try { return JSON.stringify(perLang?.[activeLang]?.[key] ?? null, null, 2); } catch { return "null"; }
  };

  const save = () => {
    if (!editing) return;
    let dataJson: string;

    if (perLang && skeleton) {
      // json draft'ları perLang'e uygula (validasyon)
      const pl: Record<Lang, any> = JSON.parse(JSON.stringify(perLang));
      for (const dk of Object.keys(jsonDrafts)) {
        const [lang, key] = dk.split(":");
        try { pl[lang as Lang][key] = JSON.parse(jsonDrafts[dk] || "null"); }
        catch { toast.error(`Geçersiz JSON: ${LANG_LABEL[lang as Lang]} / ${key}`); return; }
      }
      dataJson = buildDataJson(skeleton, pl); // leaf düzeyinde boş→en
    } else {
      try { dataJson = JSON.stringify(JSON.parse(rawJson || "{}")); }
      catch { toast.error("DataJson geçerli bir JSON değil."); return; }
    }

    const body: any = { ...(editing.Id ? { Id: editing.Id } : {}), Order: Number(editing.Order) || 0, IsActive: !!editing.IsActive, DataJson: dataJson };
    cfg.cols.forEach((c) => { body[c] = editing[c] ?? ""; });

    setSaving(true);
    const ok = (m: string) => { setSaving(false); setOpen(false); toast.success(m || "Kaydedildi"); load(); };
    const err = () => setSaving(false);
    dispatch(
      editing.Id
        ? MKTV2[activeKey].update({ id: editing.Id, body, payload: { onSuccess: ok, onError: err } })
        : MKTV2[activeKey].create({ body, payload: { onSuccess: ok, onError: err } })
    );
  };

  const del = (row: Item) => {
    if (!window.confirm("Bu kaydı silmek istiyor musunuz?")) return;
    dispatch(MKTV2[activeKey].del({ id: row.Id, payload: { onSuccess: () => { toast.success("Silindi"); load(); }, onError: () => {} } }));
  };

  const setField = (k: string, v: any) => setEditing((e: Item) => ({ ...e, [k]: v }));

  // Bir dilde eksik (boş) text alanı var mı → sekmede uyarı noktası
  const langHasEmpty = (lang: Lang): boolean => {
    if (!perLang) return false;
    return fields.some((f) => {
      if (f.kind === "json") return false;
      const v = perLang[lang]?.[f.key];
      return v == null || (typeof v === "string" && v.trim() === "");
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="mb-3">
            <Col><h4 className="mb-1">Pazarlama Sitesi V2 — İçerik Yönetimi</h4>
              <p className="text-muted mb-0">İçerikler 8 dilde, dil sekmeleriyle düzenlenir. Boş bırakılan dil, kayıtta İngilizce ile doldurulur.</p>
            </Col>
          </Row>

          <Nav pills className="mb-3 flex-wrap">
            {TYPES.map((t) => (
              <NavItem key={t.key}>
                <NavLink href="#" active={activeKey === t.key} onClick={(e) => { e.preventDefault(); setActiveKey(t.key); }}>{t.label}</NavLink>
              </NavItem>
            ))}
          </Nav>

          {activeKey === "heroslides" ? <HeroSlidesPanel /> : (
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{cfg.label} <span className="text-muted">({rows.length})</span></h5>
                <div>
                  <Button color="light" className="me-2" onClick={load}><i className="bx bx-refresh"></i> Yenile</Button>
                  <Button color="primary" onClick={openCreate}><i className="bx bx-plus"></i> Yeni</Button>
                </div>
              </div>

              <div className="table-responsive">
                <Table className="table-nowrap align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Sıra</th>
                      <th>Başlık (TR)</th>
                      {cfg.cols.map((c) => <th key={c}>{c}</th>)}
                      <th style={{ width: 80 }}>Aktif</th>
                      <th style={{ width: 130 }} className="text-end">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={cfg.cols.length + 4} className="text-center text-muted py-4">Yükleniyor…</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={cfg.cols.length + 4} className="text-center text-muted py-4">Kayıt yok.</td></tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.Id}>
                          <td>{row.Order}</td>
                          <td className="fw-medium">{String(dig(parseData(row), cfg.titlePath) || "—")}</td>
                          {cfg.cols.map((c) => <td key={c}>{String(row[c] ?? "—").slice(0, 40)}</td>)}
                          <td>{row.IsActive ? <i className="text-success bx bx-check-square"></i> : <i className="text-danger mdi mdi-close-box-outline"></i>}</td>
                          <td className="text-end">
                            <Button size="sm" color="light" className="me-1" onClick={() => openEdit(row)}><i className="bx bx-pencil"></i></Button>
                            <Button size="sm" color="light" onClick={() => del(row)}><i className="bx bx-trash text-danger"></i></Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
          )}
        </Container>
      </div>

      <Modal isOpen={open} toggle={() => setOpen(false)} size="lg" scrollable>
        <ModalHeader toggle={() => setOpen(false)}>{editing?.Id ? "Düzenle" : "Yeni"} — {cfg.label}</ModalHeader>
        <ModalBody>
          {editing && (
            <Row className="g-3">
              {cfg.cols.map((c) => (
                <Col md={6} key={c}>
                  <Label>{c}</Label>
                  <Input value={editing[c] ?? ""} onChange={(e) => setField(c, e.target.value)} />
                </Col>
              ))}
              <Col md={3}>
                <Label>Sıra</Label>
                <Input type="number" value={editing.Order ?? 0} onChange={(e) => setField("Order", e.target.value)} />
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="mktv2-active" checked={!!editing.IsActive} onChange={(e) => setField("IsActive", e.target.checked)} />
                  <label className="form-check-label" htmlFor="mktv2-active">Aktif</label>
                </div>
              </Col>

              {/* Çoklu-dil içerik */}
              <Col md={12}>
                {perLang ? (
                  <>
                    <Label className="d-block mb-1">İçerik (dil sekmeleri)</Label>
                    <Nav pills className="mb-3 flex-wrap gap-1">
                      {LANGS.map((l) => (
                        <NavItem key={l}>
                          <NavLink
                            href="#"
                            active={activeLang === l}
                            onClick={(e) => { e.preventDefault(); setActiveLang(l); }}
                            className="py-1 px-2"
                          >
                            {LANG_LABEL[l]}
                            {l !== "en" && langHasEmpty(l) && (
                              <Badge color="warning" pill className="ms-1" style={{ fontSize: 9 }}>boş</Badge>
                            )}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                    {activeLang !== "en" && (
                      <small className="text-muted d-block mb-2">Boş bırakılan alanlar kayıtta İngilizce içerikle doldurulur.</small>
                    )}
                    {fields.length === 0 ? (
                      <small className="text-muted">Bu kayıtta çoklu-dil alanı yok.</small>
                    ) : (
                      fields.map((f) => (
                        <div className="mb-3" key={f.key}>
                          <Label className="mb-1">{f.key}{f.kind === "json" ? " (yapılandırılmış)" : ""}</Label>
                          {f.kind === "text" ? (
                            <Input
                              value={perLang[activeLang]?.[f.key] ?? ""}
                              onChange={(e) => setLocText(f.key, e.target.value)}
                            />
                          ) : f.kind === "textarea" ? (
                            <Input
                              type="textarea"
                              style={{ minHeight: 90 }}
                              value={perLang[activeLang]?.[f.key] ?? ""}
                              onChange={(e) => setLocText(f.key, e.target.value)}
                            />
                          ) : (
                            <>
                              <Input
                                type="textarea"
                                style={{ fontFamily: "monospace", fontSize: 12, minHeight: 140 }}
                                value={draftFor(f.key)}
                                onChange={(e) => setJsonDraft(f.key, e.target.value)}
                              />
                              <small className="text-muted">Bu dilin yapılandırılmış içeriği (JSON). Yapı korunmalı.</small>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </>
                ) : (
                  <>
                    <Label>DataJson (yeni kayıt — {`{en, tr, ...}`})</Label>
                    <Input type="textarea" style={{ fontFamily: "monospace", fontSize: 12, minHeight: 300 }} value={rawJson} onChange={(e) => setRawJson(e.target.value)} />
                    <small className="text-muted">Yeni kayıt için içerik yapısını JSON olarak girin. Kaydedip tekrar açtığınızda dil sekmeleriyle düzenleyebilirsiniz.</small>
                  </>
                )}
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setOpen(false)}>Kapat</Button>
          <Button color="primary" disabled={saving} onClick={save}>{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default MarketingV2Page;
